import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import memberService from '../../services/memberService';
import organizationService from '../../services/organizationService';
import { addNotification } from '../../store/slices/uiSlice';
import api from '../../services/api';
import './MemberList.css';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import * as XLSX from 'xlsx';

const MemberList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState('regular');
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Organization structure data
  const [orgStructure, setOrgStructure] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [zones, setZones] = useState([]);
  const [groups, setGroups] = useState([]);
  
  // Filter values
  const [districtFilter, setDistrictFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  
  // Combobox states
  const [districtQuery, setDistrictQuery] = useState('');
  const [zoneQuery, setZoneQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showZoneDropdown, setShowZoneDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  
  // Search autocomplete
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 20,
    total: 0
  });
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Load organization structure on mount
  useEffect(() => {
    loadOrganizationStructure();
  }, []);

  const loadOrganizationStructure = async () => {
    try {
      const data = await organizationService.getOrganizationTree();
      setOrgStructure(data);
      
      // Extract unique districts
      const districtList = data.map(d => d.name);
      setDistricts(districtList);
      
      // Extract all zones
      const allZones = data.flatMap(d => d.zones || []).map(z => z.name);
      setZones([...new Set(allZones)]);
      
      // Extract all groups
      const allGroups = data.flatMap(d => 
        (d.zones || []).flatMap(z => (z.groups || []).map(g => g.name))
      );
      setGroups([...new Set(allGroups)]);
    } catch (err) {
      console.error('Error loading organization structure:', err);
    }
  };

  // Fetch autocomplete suggestions for search only
  const fetchAutocomplete = async (fieldType, value) => {
    if (!value || value.length < 1) {
      if (fieldType === 'search') setSearchSuggestions([]);
      return;
    }

    try {
      const data = await memberService.getAutocomplete(fieldType, value);
      if (fieldType === 'search') setSearchSuggestions(data.suggestions || []);
    } catch (err) {
      console.error(`Error fetching ${fieldType} autocomplete:`, err);
    }
  };

  // Handle search input change - only for fetching suggestions
  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (value.length > 0) {
      fetchAutocomplete('search', value);
      setShowSearchDropdown(true);
    } else {
      setSearch(''); // Clear the filter when input is cleared
      setShowSearchDropdown(false);
    }
  };

  // Handle search selection from dropdown
  const handleSearchSelect = (suggestion) => {
    setSearch(suggestion);
    setSearchInput(suggestion);
    setShowSearchDropdown(false);
  };

  // District combobox handlers
  const filteredDistricts = districtQuery === ''
    ? districts
    : districts.filter((district) =>
        district.toLowerCase().includes(districtQuery.toLowerCase())
      );

  const handleDistrictSelect = (district) => {
    setDistrictFilter(district);
    setDistrictQuery(district);
    setShowDistrictDropdown(false);
    
    // Update available zones based on selected district
    if (district) {
      const selectedDist = orgStructure.find(d => d.name === district);
      if (selectedDist) {
        const zoneList = (selectedDist.zones || []).map(z => z.name);
        setZones(zoneList);
      }
    } else {
      // Reset to all zones
      const allZones = orgStructure.flatMap(d => d.zones || []).map(z => z.name);
      setZones([...new Set(allZones)]);
    }
    
    // Clear zone and group filters when district changes
    setZoneFilter('');
    setZoneQuery('');
    setGroupFilter('');
    setGroupQuery('');
  };

  // Zone combobox handlers
  const filteredZones = zoneQuery === ''
    ? zones
    : zones.filter((zone) =>
        zone.toLowerCase().includes(zoneQuery.toLowerCase())
      );

  const handleZoneSelect = (zone) => {
    setZoneFilter(zone);
    setZoneQuery(zone);
    setShowZoneDropdown(false);
    
    // Update available groups based on selected zone
    if (zone) {
      const selectedZone = orgStructure
        .flatMap(d => d.zones || [])
        .find(z => z.name === zone);
      if (selectedZone) {
        const groupList = (selectedZone.groups || []).map(g => g.name);
        setGroups(groupList);
      }
    } else {
      // Reset to all groups
      const allGroups = orgStructure.flatMap(d => 
        (d.zones || []).flatMap(z => (z.groups || []).map(g => g.name))
      );
      setGroups([...new Set(allGroups)]);
    }
    
    // Clear group filter when zone changes
    setGroupFilter('');
    setGroupQuery('');
  };

  // Group combobox handlers
  const filteredGroups = groupQuery === ''
    ? groups
    : groups.filter((group) =>
        group.toLowerCase().includes(groupQuery.toLowerCase())
      );

  const handleGroupSelect = (group) => {
    setGroupFilter(group);
    setGroupQuery(group);
    setShowGroupDropdown(false);
  };

  // Fetch members
  const fetchMembers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (memberTypeFilter) params.member_type = memberTypeFilter;
      if (groupFilter) params.group = groupFilter;
      if (zoneFilter) params.zone = zoneFilter;
      if (districtFilter) params.district = districtFilter;

      const response = await memberService.getMembers(params);
      setMembers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.total
      }));
    } catch (err) {
      setError(t('member.list.error'));
      console.error('Error fetching members:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [pagination.skip, pagination.limit, statusFilter, memberTypeFilter, groupFilter, zoneFilter, districtFilter]);

  // Handle tab change - reset pagination when switching tabs
  const handleTabChange = (newType) => {
    setMemberTypeFilter(newType);
    setPagination(prev => ({ ...prev, skip: 0 }));
    // Keep existing filters to allow continuing search with same criteria
  };

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== undefined) {
        fetchMembers();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Handle delete - show modal
  const handleDeleteClick = (memberId, memberName) => {
    setMemberToDelete({ id: memberId, name: memberName });
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (deleteConfirmText.toLowerCase() !== 'delete') {
      return;
    }

    try {
      await memberService.deleteMember(memberToDelete.id);
      dispatch(addNotification({
        type: 'success',
        title: t('common.successTitle'),
        message: t('member.list.deleteSuccess')
      }));
      setShowDeleteModal(false);
      setMemberToDelete(null);
      setDeleteConfirmText('');
      fetchMembers();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        title: t('common.errorTitle'),
        message: t('member.list.deleteError')
      }));
      console.error('Error deleting member:', err);
    }
  };
  
  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
    setDeleteConfirmText('');
  };

  // Pagination handlers
  const handlePrevPage = () => {
    setPagination(prev => ({
      ...prev,
      skip: Math.max(0, prev.skip - prev.limit)
    }));
  };

  const handleNextPage = () => {
    setPagination(prev => ({
      ...prev,
      skip: prev.skip + prev.limit
    }));
  };

  // Get status badge class
  const getStatusBadge = (status) => {
    const badgeClass = {
      active: 'badge-success',
      inactive: 'badge-secondary',
      suspended: 'badge-warning'
    };
    return badgeClass[status] || 'badge-secondary';
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      active: t('member.list.statusActive'),
      inactive: t('member.list.statusInactive'),
      suspended: t('member.list.statusSuspended')
    };
    return labelMap[status] || status || t('common.na');
  };

  const getGenderLabel = (sex) => {
    if (!sex) return '—';
    const key = String(sex).trim().toLowerCase();
    if (key === 'male' || key === '男' || key === 'm') return t('member.form.options.genderMale');
    if (key === 'female' || key === '女' || key === 'f') return t('member.form.options.genderFemale');
    if (key === 'other' || key === '其他') return t('member.form.options.genderOther');
    return sex;
  };

  const getMemberRoleLabel = (memberType, orgLeadership) => {
    const typeMap = {
      regular: t('member.detail.roleRegular'),
      associate: t('member.detail.roleAssociate'),
      temporary: t('member.detail.roleTemporary'),
    };
    const typeLabel = typeMap[memberType] || memberType || '';
    const leadLabel =
      orgLeadership === 'district_pastor' ? '區牧' :
      orgLeadership === 'zone_leader' ? '區長' : '';
    if (typeLabel && leadLabel) return `${typeLabel} · ${leadLabel}`;
    return typeLabel || leadLabel || '—';
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  // Export all members
  const handleExportAll = async () => {
    try {
      const response = await api.get('/api/v1/members/export', {
        params: { member_type: memberTypeFilter || undefined },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `members_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(addNotification({ type: 'success', message: t('member.list.export.success') }));
    } catch (err) {
      console.error('Export failed:', err);
      dispatch(addNotification({ type: 'error', message: t('member.list.export.error') }));
    }
  };

  // Download import template
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/v1/members/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'member_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(addNotification({ type: 'success', message: t('member.list.template.downloaded') }));
    } catch (err) {
      console.error('Template download failed:', err);
      dispatch(addNotification({ type: 'error', message: t('member.list.template.error') }));
    }
  };

  // Handle import file selection → parse CSV → show preview popup
  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      dispatch(addNotification({ type: 'error', message: t('member.list.import.invalidFileType') }));
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (rows.length < 2) {
        dispatch(addNotification({ type: 'error', message: t('member.list.import.emptyFile') }));
        return;
      }
      const headers = rows[0].map(h => String(h).trim());
      const worshipIdx = headers.indexOf('worship_id');
      const xlsxWorshipIds = rows.slice(1).map(row => {
        return String(row[worshipIdx] || '').trim();
      }).filter(id => id.length > 0);

      // Compare against existing members
      const existingRes = await memberService.getMembers({ limit: 99999 });
      const existingIds = new Set((existingRes.data || []).map(m => m.worship_id).filter(Boolean));

      const totalRows = rows.length - 1;
      const toUpdate = xlsxWorshipIds.filter(id => existingIds.has(id)).length;
      const toCreate = totalRows - toUpdate;

      setImportPreview({ toCreate, toUpdate, totalRows, file });
    } catch (err) {
      console.error('Preview failed:', err);
      dispatch(addNotification({ type: 'error', message: t('member.list.import.error') }));
    }
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importPreview.file);
    setImportPreview(null);
    try {
      const response = await api.post('/api/v1/members/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      dispatch(addNotification({
        type: 'success',
        message: t('member.list.import.success', {
          created: response.data.created,
          updated: response.data.updated,
          errors: response.data.errors
        })
      }));
      fetchMembers();
    } catch (err) {
      console.error('Import failed:', err);
      const detail = err.response?.data?.detail;
      const safeMsg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join('; ')
        : (typeof detail === 'string' ? detail : t('member.list.import.error'));
      dispatch(addNotification({
        type: 'error',
        message: safeMsg
      }));
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => setImportPreview(null);

  return (
    <div className="member-list-container">
      <div className="member-list-header">
        <h2>{t('member.list.title')}</h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Export All */}
          <button
            onClick={handleExportAll}
            className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1"
          >
            <i className="fas fa-file-export"></i> {t('member.list.export.button')}
          </button>
          {/* Download Template */}
          <button
            onClick={handleDownloadTemplate}
            className="bg-purple-500 hover:bg-purple-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1"
          >
            <i className="fas fa-file-download"></i> {t('member.list.template.button')}
          </button>
          {/* Import All */}
          <button
            onClick={handleImportClick}
            disabled={importing}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-1 disabled:opacity-50"
          >
            <i className="fas fa-file-import"></i> {importing ? t('member.list.import.importing') : t('member.list.import.button')}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
          {/* Add New */}
          <Link to="/members/new" className="btn btn-primary">
            <i className="fas fa-plus"></i> {t('member.list.add')}
          </Link>
        </div>
      </div>

      {/* Member Type Tabs */}
      <div className="member-tabs">
        <button
          className={`member-tab ${memberTypeFilter === 'regular' ? 'active' : ''}`}
          onClick={() => handleTabChange('regular')}
        >
          <i className="fas fa-user-check"></i>
          {t('member.tabs.fullMember', { defaultValue: '正式會友' })}
        </button>
        <button
          className={`member-tab ${memberTypeFilter === 'associate' ? 'active' : ''}`}
          onClick={() => handleTabChange('associate')}
        >
          <i className="fas fa-user"></i>
          {t('member.tabs.associate', { defaultValue: '準會友' })}
        </button>
        <button
          className={`member-tab ${memberTypeFilter === 'temporary' ? 'active' : ''}`}
          onClick={() => handleTabChange('temporary')}
        >
          <i className="fas fa-user-clock"></i>
          {t('member.tabs.temporary', { defaultValue: '臨時會友' })}
        </button>
      </div>

      {/* Filters */}
      <div className="member-list-filters">
        <div className="filter-group">
          <div className="autocomplete-wrapper">
            <input
              type="text"
              className="form-control"
              placeholder={t('member.list.searchPlaceholder', { defaultValue: 'Search by name, worship number, mobile, NIM' })}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => searchInput && setShowSearchDropdown(true)}
              onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
            />
            {showSearchDropdown && searchSuggestions.length > 0 && (
              <div className="autocomplete-dropdown">
                {searchSuggestions.map((suggestion, idx) => (
                  <div
                    key={idx}
                    className="autocomplete-item"
                    onClick={() => handleSearchSelect(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="filter-group">
          <select
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">{t('member.list.statusAll')}</option>
            <option value="active">{t('member.list.statusActive')}</option>
            <option value="inactive">{t('member.list.statusInactive')}</option>
            <option value="suspended">{t('member.list.statusSuspended')}</option>
          </select>
        </div>
        <div className="filter-group">
          <div className="combobox-wrapper">
            <input
              type="text"
              className="form-control"
              placeholder={t('member.form.labels.cellGroup3', { defaultValue: 'District' })}
              value={districtQuery}
              onChange={(e) => setDistrictQuery(e.target.value)}
              onFocus={() => setShowDistrictDropdown(true)}
              onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
            />
            {showDistrictDropdown && filteredDistricts.length > 0 && (
              <div className="combobox-dropdown">
                <div
                  className="combobox-item"
                  onClick={() => handleDistrictSelect('')}
                >
                  <span className="text-gray-500">{t('common.all', { defaultValue: 'All Districts' })}</span>
                </div>
                {filteredDistricts.map((district, idx) => (
                  <div
                    key={idx}
                    className={`combobox-item ${districtFilter === district ? 'active' : ''}`}
                    onClick={() => handleDistrictSelect(district)}
                  >
                    {district}
                    {districtFilter === district && (
                      <i className="fas fa-check ml-auto"></i>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="filter-group">
          <div className="combobox-wrapper">
            <input
              type="text"
              className="form-control"
              placeholder={t('member.form.labels.cellGroup2', { defaultValue: 'Zone' })}
              value={zoneQuery}
              onChange={(e) => setZoneQuery(e.target.value)}
              onFocus={() => setShowZoneDropdown(true)}
              onBlur={() => setTimeout(() => setShowZoneDropdown(false), 200)}
              disabled={!districtFilter && zones.length === 0}
            />
            {showZoneDropdown && filteredZones.length > 0 && (
              <div className="combobox-dropdown">
                <div
                  className="combobox-item"
                  onClick={() => handleZoneSelect('')}
                >
                  <span className="text-gray-500">{t('common.all', { defaultValue: 'All Zones' })}</span>
                </div>
                {filteredZones.map((zone, idx) => (
                  <div
                    key={idx}
                    className={`combobox-item ${zoneFilter === zone ? 'active' : ''}`}
                    onClick={() => handleZoneSelect(zone)}
                  >
                    {zone}
                    {zoneFilter === zone && (
                      <i className="fas fa-check ml-auto"></i>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="filter-group">
          <div className="combobox-wrapper">
            <input
              type="text"
              className="form-control"
              placeholder={t('member.form.labels.cellGroup1', { defaultValue: 'Group' })}
              value={groupQuery}
              onChange={(e) => setGroupQuery(e.target.value)}
              onFocus={() => setShowGroupDropdown(true)}
              onBlur={() => setTimeout(() => setShowGroupDropdown(false), 200)}
              disabled={!zoneFilter && groups.length === 0}
            />
            {showGroupDropdown && filteredGroups.length > 0 && (
              <div className="combobox-dropdown">
                <div
                  className="combobox-item"
                  onClick={() => handleGroupSelect('')}
                >
                  <span className="text-gray-500">{t('common.all', { defaultValue: 'All Groups' })}</span>
                </div>
                {filteredGroups.map((group, idx) => (
                  <div
                    key={idx}
                    className={`combobox-item ${groupFilter === group ? 'active' : ''}`}
                    onClick={() => handleGroupSelect(group)}
                  >
                    {group}
                    {groupFilter === group && (
                      <i className="fas fa-check ml-auto"></i>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="sr-only">{t('loading.loading')}</span>
          </div>
          <p>{t('member.list.loading')}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-link" onClick={fetchMembers}>
            {t('member.list.retry')}
          </button>
        </div>
      )}

      {/* Members Table */}
      {!loading && !error && (
        <>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{t('member.list.table.worship')}</th>
                  <th>{t('member.list.table.nickname')}</th>
                  <th>{t('member.list.table.nameChi')}</th>
                  <th>{t('member.list.table.nameEng')}</th>
                  <th>{t('member.list.table.gender')}</th>
                  <th>{t('member.list.table.role')}</th>
                  <th>{t('member.list.table.group')}</th>
                  <th>{t('member.list.table.zone')}</th>
                  <th>{t('member.list.table.district')}</th>
                  <th>{t('member.list.table.mobile')}</th>
                  <th>{t('member.list.table.email')}</th>
                  <th>{t('member.list.table.status')}</th>
                  <th>{t('member.list.table.joinDate')}</th>
                  <th>{t('member.list.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="text-center">
                      {t('member.list.empty')}
                    </td>
                  </tr>
                ) : (
                  members.map((member) => (
                    <tr key={member.id}>
                      <td>{(member.worship_id || '').trim() || '—'}</td>
                      <td className="member-nickname-cell">{(member.nickname || '').trim() || '—'}</td>
                      <td>
                        <Link to={`/members/${member.id}`} className="member-name-link">
                          {member.chi_name}
                        </Link>
                      </td>
                      <td>{member.eng_name || '—'}</td>
                      <td>{getGenderLabel(member.sex_name)}</td>
                      <td className="member-role-cell">
                        {getMemberRoleLabel(member.member_type, member.org_leadership)}
                      </td>
                      <td className="member-groups-cell" title={member.groups || ''}>
                        {(() => {
                          if (member.org_leadership === 'zone_leader' || member.org_leadership === 'district_pastor') {
                            return 'NA';
                          }
                          const raw = (member.groups || '').trim();
                          if (!raw) return '—';
                          const parts = raw.split(';').map((s) => s.trim()).filter(Boolean);
                          if (parts.length <= 1) return parts[0] || '—';
                          return parts.map((p) => p.replace(/小組$/, '')).join(';');
                        })()}
                      </td>
                      <td className="member-org-cell">
                        {member.org_leadership === 'district_pastor'
                          ? 'NA'
                          : ((member.zones || '').trim() || '—')}
                      </td>
                      <td className="member-org-cell">{(member.districts || '').trim() || '—'}</td>
                      <td>{(member.mobile || member.tel_day || '').trim() || '—'}</td>
                      <td className="member-email-cell">{(member.email || '').trim() || '—'}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(member.status_name)}`}>
                          {getStatusLabel(member.status_name)}
                        </span>
                      </td>
                      <td>{formatDate(member.date_of_join)}</td>
                      <td className="actions-cell">
                        <Link
                          to={`/members/${member.id}`}
                          className="btn btn-sm btn-info"
                          title={t('member.list.view')}
                        >
                          <i className="fas fa-eye"></i> {t('member.list.view')}
                        </Link>
                        <Link
                          to={`/members/${member.id}/edit`}
                          className="btn btn-sm btn-warning"
                          title={t('member.list.edit')}
                        >
                          <i className="fas fa-edit"></i> {t('member.list.edit')}
                        </Link>
                        <button
                          onClick={() => handleDeleteClick(member.id, member.chi_name)}
                          className="btn btn-sm btn-danger"
                          title={t('member.list.delete')}
                        >
                          <i className="fas fa-trash"></i> {t('member.list.delete')}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              {t('member.list.pagination.showing', {
                from: pagination.skip + 1,
                to: Math.min(pagination.skip + pagination.limit, pagination.total),
                total: pagination.total
              })}
            </div>
            <div className="pagination-controls">
              <button
                className="btn btn-secondary"
                onClick={handlePrevPage}
                disabled={pagination.skip === 0}
              >
                {t('member.list.pagination.previous')}
              </button>
              <span className="page-info">
                {t('member.list.pagination.pageOf', { page: currentPage, total: totalPages || 1 })}
              </span>
              <button
                className="btn btn-secondary"
                onClick={handleNextPage}
                disabled={pagination.skip + pagination.limit >= pagination.total}
              >
                {t('member.list.pagination.next')}
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('member.list.deleteModal.title')}</h3>
              <button className="modal-close" onClick={handleDeleteCancel}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-triangle"></i>
                <strong>{t('member.list.deleteModal.warning')}</strong>
              </div>
              <p>
                {t('member.list.deleteModal.message', { name: memberToDelete?.name })}
              </p>
              <p className="text-muted">
                {t('member.list.deleteModal.instruction')}
              </p>
              <div className="form-group">
                <input
                  type="text"
                  className="form-control"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('member.list.deleteModal.placeholder')}
                  autoFocus
                />
              </div>
              {deleteConfirmText && deleteConfirmText.toLowerCase() !== 'delete' && (
                <p className="text-danger small">
                  <i className="fas fa-times-circle"></i> {t('member.list.deleteModal.mismatch')}
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'delete'}
              >
                <i className="fas fa-trash"></i> {t('member.list.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Confirmation Modal */}
      {importPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-yellow-100 p-2 rounded-full">
                <i className="fas fa-file-import text-yellow-600 text-xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('member.list.import.confirm.title')}</h3>
            </div>
            <p className="text-gray-600 text-sm mb-4">{t('member.list.import.confirm.message')}</p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('member.list.import.confirm.totalRows')}</span>
                <span className="font-semibold text-gray-900">{importPreview.totalRows}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                <span className="text-sm text-green-700">
                  <i className="fas fa-plus-circle mr-1"></i>{t('member.list.import.confirm.toCreate')}
                </span>
                <span className="font-semibold text-green-700 text-lg">{importPreview.toCreate}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-700">
                  <i className="fas fa-edit mr-1"></i>{t('member.list.import.confirm.toUpdate')}
                </span>
                <span className="font-semibold text-blue-700 text-lg">{importPreview.toUpdate}</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelImport}
                className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                {t('member.list.import.confirm.cancel')}
              </button>
              <button
                onClick={handleConfirmImport}
                disabled={importing}
                className="px-5 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white font-medium transition-colors disabled:opacity-50"
              >
                {importing ? t('member.list.import.importing') : t('member.list.import.confirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberList;
